
import { Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import { useMemo } from "react";
import { rtfoConversionFactors } from "./rtfoConversionFactors";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    marginTop: 0,
    marginBottom: 0,
    border: 'none'
  },
});

export const RTFOPumpdownDataTable = ({ currentRTFO, darkMode }) => {

  
    // STYLES
    const classes = useStyles();
    const units = currentRTFO?.settings?.units || 'metric';

    const rows = useMemo(() => {
      return [...currentRTFO.stages]?.reverse()?.map(stageName => {
        const stage = currentRTFO?.stageData?.[stageName];

        const pumpdownParams = stage?.pumpdownParams;

        return {
          id: stageName,
          stageName,
          wellName: stage?.wellName,
          stageNumber: (stage?.stageNumber), 
          pumpdownISIP: Number((pumpdownParams?.pumpdownISIP * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
          postPerfISIP: Number((pumpdownParams?.postPerfISIP * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
          postPlugISIP: Number((pumpdownParams?.postPlugISIP * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
          customISIP: Number((pumpdownParams?.customISIP * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
          isolationIssue: pumpdownParams?.postPerfISIP - pumpdownParams?.pumpdownISIP < 1 ? 1 : 0,
          pressureTestIncrease: Number((pumpdownParams?.pressureTestIncrease * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0, 
          pressureTestDuration: Number(pumpdownParams?.pressureTestDuration?.toFixed(3)) || 0, 
          pressureTestOverISIPStart: Number((pumpdownParams?.pressureTestOverISIPStart * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0, 
          pressureTestOverISIPEnd: Number((pumpdownParams?.pressureTestOverISIPEnd * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0
        };
      });
    }, [currentRTFO?.stageData, currentRTFO.stages, units]);
  
  try {

    const columns = [
      {
        field: 'stageName',
        headerName: 'Stage Name',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellName',
        headerName: 'Well',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'stageNumber',
        headerName: 'Stage No.',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pumpdownISIP',
        headerName: `Pumpdown ISIP (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'postPerfISIP',
        headerName: `Post Perf ISIP (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'postPlugISIP',
        headerName: `Post Plug ISIP (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'customISIP',
        headerName: `Custom ISIP (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'isolationIssue',
        headerName: 'Isolation Issue',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: 'pressureTestIncrease',
        headerName: `Press. Test Increase (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: 'pressureTestDuration',
        headerName: 'Press. Test Duration (min)',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: 'pressureTestOverISIPStart',
        headerName: `Press. Test Over ISIP, Start (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: 'pressureTestOverISIPEnd',
        headerName: `Press. Test Over ISIP, End (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
      },
    ];

    return (
      <Paper className={classes.root} elevation={0}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10,25,50,100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: CustomToolbar,
          }}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: darkMode ? '#263238' : 'whitesmoke'
            },
            '& .super-app-theme--Current': {
              fontWeight: 'bold',
              backgroundColor: 'rgba(7, 88, 183, 0.25)' // highlight the current row
            }
          }}
          getRowClassName={(params) => {
            return (params?.row?.stageName === currentRTFO?.currentStageName) ? `super-app-theme--Current` : '';
          }}
          density="compact"
          style={{fontSize: '12px'}}
        />
      </Paper>
    );
  } catch (error) {
    return <></>
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarFilterButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarDensitySelector style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarExport style={{ color: darkMode && 'lightgrey' }} />
      </GridToolbarContainer>
    );
  };
};

